interface LeafletDefaults {
  lat: number
  lng: number
  filename: string
}

interface FrontPageMapDefaults {
  filename_part: string
  name: string
}

interface HomepageDefaultsResponse {
  selected_feature: string | null
  leaflet: LeafletDefaults | null
  front_page_map: FrontPageMapDefaults | null
}

interface FcgonMapConfig {
  lat: number
  lng: number
  filename: string
}

declare namespace FCGON {
  const maps: FcgonMapConfig[] | undefined
}

const PLACEHOLDER_VALUE = "-"

// The homepage HTML is edge-cached and rendered visitor-independent; this
// module fetches the visitor's defaults (Web::HomepageDefaultsController)
// and applies them to the resort dropdowns, the leaflet map center, and the
// snow maps SEO section.
export class HomepageDefaults {
  static async init(parent: ParentNode = document): Promise<void> {
    if (!parent.querySelector(".homepage-hero")) return

    try {
      const response = await fetch("/homepage_defaults")
      if (!response.ok) return
      const defaults = (await response.json()) as HomepageDefaultsResponse
      new HomepageDefaults(defaults).apply()
    } catch {
      // the generic defaults baked into the cached page remain
    }
  }

  constructor(private defaults: HomepageDefaultsResponse) {}

  apply() {
    this.applyDropdowns()
    this.applyLeaflet()
    this.applyFrontPageMap()
  }

  private applyDropdowns() {
    const feature = this.defaults.selected_feature
    const select = document.querySelector<HTMLSelectElement>(".js-dropdown-feature")
    if (!feature || !select) return
    if (select.value !== PLACEHOLDER_VALUE) return // the visitor was faster

    const option = Array.from(select.options).find((o) => o.value === feature)
    if (!option) return

    select.value = feature
    select.dispatchEvent(new Event("change", { bubbles: true })) // ResortDropdowns loads the resorts
  }

  private applyLeaflet() {
    const center = this.defaults.leaflet
    const root = document.querySelector<HTMLElement>(".leaflet[data-map-id]")
    if (!center || !root || typeof FCGON === "undefined") return

    const config = FCGON.maps && FCGON.maps[Number(root.dataset.mapId)]
    if (!config) return

    config.lat = center.lat
    config.lng = center.lng
    config.filename = center.filename
  }

  private applyFrontPageMap() {
    const map = this.defaults.front_page_map
    const root = document.querySelector<HTMLElement>(".snow-maps-seo-section")
    if (!map || !root) return

    root.querySelectorAll<HTMLAnchorElement>('a[href^="/maps/dynamic/"]').forEach((link) => {
      link.href = `/maps/dynamic/${map.filename_part}` // eslint-disable-line no-param-reassign
    })
    const image = root.querySelector<HTMLImageElement>('img[src^="/map/3days/"]')
    if (image) {
      image.src = `/map/3days/${map.filename_part}.3days.jpg`
      image.alt = `${map.name} Snow Forecast`
    }
  }
}
